import {runInAction} from 'mobx';
import {logger} from '../../logger';
import {IPriceSummary, IPriceSummaryLine} from '../i';
import {isRequestCanceled} from '../../global/request-error';

export const PRICING_REQUEST_FAILED_MSG = 'Unable to update the price. The last valid price is still shown.';
/** The very first request failed: no price has been shown yet, so there is no "last valid price" to point at. */
export const PRICING_INITIAL_REQUEST_FAILED_MSG = 'Unable to load the price. Please refresh the page or try again in a moment.';
export const DOCUMENT_SIZE_RELOAD_MSG = 'This product\'s size options have changed. Please reload the product page.';

export type PricingUi = {
    price: number;
    price_discount: number;
    price_summary: IPriceSummary | null;
    /** Upload & Print (FS 10214): the last response's upload_fee — the store's fee per uploaded image by the server rule (view.product.UploadFee); 0 until the first response or when the store charges none. */
    upload_fee: number;
    /** The last response's upload_fee_applied: whether price / price_discount already include upload_fee. */
    upload_fee_applied: boolean;
    /** Set once the first pricing response has been applied (or the server's opening price was seeded); the UI withholds the price until then. */
    price_resolved: boolean;
    /** True while price / price_discount hold the server-rendered opening price (opening-seed.ts) and no live response has replaced it yet. */
    price_seeded: boolean;
    pricing_loading: boolean;
    pricing_error: string;
};

export type PricingControllerDeps<P> = {
    getUi: () => PricingUi;
    getPayload: () => P;
    requestPricing: (payload: P, signal: AbortSignal) => Promise<unknown>;
    /**
     * Runs synchronously on the controller's FIRST pricing() call only, with the payload getPayload() just returned and
     * before the request is sent. The product page seeds the server-rendered opening price here, against the exact state
     * the request carries (pulse() normalizes ps before this point). A later resetPricingFlight() does not re-arm it.
     */
    onFirstRequest?: (ui: PricingUi, payload: P) => void;
    /**
     * Runs inside the success action, BEFORE the response values are written, when the price on screen is still the
     * seeded one (ui.price_seeded); the controller clears price_seeded afterwards whether or not the hook is wired.
     */
    onSeededResponse?: (ui: PricingUi, price: number, priceDiscount: number, payload: P) => void;
};

// Moved to global/request-error.ts (logger.ts drops cancellations too); re-exported for the existing importers.
export {isRequestCanceled};

export const isFiniteNonNegativeNumber = (value: unknown): value is number => {
    return typeof value === 'number' && Number.isFinite(value) && value >= 0;
};

const isFiniteNumber = (value: unknown): value is number => {
    return typeof value === 'number' && Number.isFinite(value);
};

const isValidPriceSummaryLine = (value: unknown): value is IPriceSummaryLine => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
        return false;
    }
    const line = value as IPriceSummaryLine;
    if (typeof line.label !== 'string') {
        return false;
    }
    if (!isFiniteNumber(line.amount)) {
        return false;
    }
    // Omitted note is valid; only a present non-string (other than null) is malformed.
    if (line.note != null && typeof line.note !== 'string') {
        return false;
    }
    return true;
};

export type PriceSummaryParseResult = {ok: true; summary: IPriceSummary | null} | {ok: false};

/** null/undefined is a legitimate omitted summary. Any other invalid shape is malformed. */
export const parsePriceSummary = (value: unknown): PriceSummaryParseResult => {
    if (value == null) {
        return {ok: true, summary: null};
    }
    if (typeof value !== 'object' || Array.isArray(value)) {
        return {ok: false};
    }
    const raw = value as IPriceSummary;
    if (!Array.isArray(raw.lines) || !raw.lines.every(isValidPriceSummaryLine)) {
        return {ok: false};
    }
    if (!isFiniteNumber(raw.total) || !isFiniteNumber(raw.discounted_total)) {
        return {ok: false};
    }
    return {ok: true, summary: raw};
};

export const createPricingController = <P = unknown>(deps: PricingControllerDeps<P>): {pricing: () => Promise<void>; resetPricingFlight: () => void} => {
    let pricingRequestSeq = 0;
    let firstRequestPending = true;
    let pricingAbortController: AbortController | null = null;

    const isLatestPricingRequest = (requestId: number): boolean => requestId === pricingRequestSeq;

    const settlePricingController = (controller: AbortController): void => {
        if (pricingAbortController === controller) {
            pricingAbortController = null;
        }
    };

    const applyLatestPricingFailure = (requestId: number, controller: AbortController, logValue?: unknown, shopperMessage?: string): void => {
        if (!isLatestPricingRequest(requestId)) {
            return;
        }
        settlePricingController(controller);
        if (logValue !== undefined) {
            logger.error(logValue);
        }
        runInAction(() => {
            const ui = deps.getUi();
            ui.pricing_error = shopperMessage ?? (ui.price_resolved ? PRICING_REQUEST_FAILED_MSG : PRICING_INITIAL_REQUEST_FAILED_MSG);
            ui.pricing_loading = false;
        });
    };

    const resetPricingFlight = (): void => {
        pricingRequestSeq += 1;
        const pending = pricingAbortController;
        pricingAbortController = null;
        pending?.abort();
        runInAction(() => {
            deps.getUi().pricing_loading = false;
        });
    };

    const pricing = async (): Promise<void> => {
        const payload = deps.getPayload();
        if (firstRequestPending) {
            firstRequestPending = false;
            if (deps.onFirstRequest) {
                try {
                    deps.onFirstRequest(deps.getUi(), payload);
                } catch (err) {
                    // The seed is an enhancement: a fault in it must never stop the request below.
                    logger.error(err);
                }
            }
        }
        const requestId = ++pricingRequestSeq;
        const controller = new AbortController();
        const previous = pricingAbortController;
        pricingAbortController = controller;
        previous?.abort();

        runInAction(() => {
            deps.getUi().pricing_loading = true;
        });

        try {
            const result: unknown = await deps.requestPricing(payload, controller.signal);
            if (!isLatestPricingRequest(requestId)) {
                return;
            }

            if (!result || typeof result !== 'object') {
                applyLatestPricingFailure(requestId, controller, 'pricing: invalid response shape');
                return;
            }

            const body = result as {success?: unknown; error_code?: unknown; result_decimal?: unknown; result_decimal_discount?: unknown; price_summary?: unknown; upload_fee?: unknown; upload_fee_applied?: unknown};
            if (body.success === 0 && body.error_code === 'document_size_not_supported') {
                applyLatestPricingFailure(requestId, controller, 'pricing: document size not supported', DOCUMENT_SIZE_RELOAD_MSG);
                return;
            }
            if (body.success !== 1 || !isFiniteNonNegativeNumber(body.result_decimal) || !isFiniteNonNegativeNumber(body.result_decimal_discount)) {
                applyLatestPricingFailure(requestId, controller, body.success === 0 ? 'pricing: success=0' : 'pricing: invalid price fields');
                return;
            }

            const parsedSummary = parsePriceSummary(body.price_summary);
            if (!parsedSummary.ok) {
                applyLatestPricingFailure(requestId, controller, 'pricing: invalid price_summary');
                return;
            }

            const price = body.result_decimal;
            const priceDiscount = body.result_decimal_discount;
            const priceSummary = parsedSummary.summary;
            // Upload & Print (FS 10214): optional fields — a response without them is still valid and means "no fee".
            const uploadFee = isFiniteNonNegativeNumber(body.upload_fee) ? body.upload_fee : 0;
            const uploadFeeApplied = body.upload_fee_applied === true;

            settlePricingController(controller);
            runInAction(() => {
                const ui = deps.getUi();
                if (ui.price_seeded && deps.onSeededResponse) {
                    try {
                        deps.onSeededResponse(ui, price, priceDiscount, payload);
                    } catch (err) {
                        logger.error(err);
                    }
                }
                ui.price_seeded = false;
                ui.price = price;
                ui.price_discount = priceDiscount;
                ui.upload_fee = uploadFee;
                ui.upload_fee_applied = uploadFeeApplied;
                ui.price_summary = priceSummary;
                ui.price_resolved = true;
                ui.pricing_error = '';
                ui.pricing_loading = false;
            });
        } catch (err) {
            if (isRequestCanceled(err) || !isLatestPricingRequest(requestId)) {
                return;
            }
            applyLatestPricingFailure(requestId, controller);
        }
    };

    return {pricing, resetPricingFlight};
};
